const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
        // Only unobserve AFTER animation plays — prevents premature disconnect
        observer.unobserve(elementTarget);
      }
      // If no offscreen class, stay observed so it can fire again later
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

// Shared IntersectionObserver reused across calls
let scrollObserver = null;

function getScrollObserver() {
  if (!scrollObserver) {
    scrollObserver = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -10px 0px', // was -50px, reduced dead zone at viewport bottom
    });
  }
  return scrollObserver;
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  // Skip animations if user prefers reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = getScrollObserver();
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Watch for dynamically injected scroll-trigger elements (lazy sections, AJAX content)
function watchForNewScrollTriggers() {
  if (typeof MutationObserver === 'undefined') return;

  // Debounce: collapse rapid-fire mutations into one re-scan
  let debounceTimer = null;

  const mo = new MutationObserver((mutations) => {
    let hasNew = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (
          node.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) ||
          node.querySelector('.' + SCROLL_ANIMATION_TRIGGER_CLASSNAME)
        ) {
          hasNew = true;
        }
      });
    });
    if (hasNew) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        requestAnimationFrame(() => initializeScrollAnimationTrigger());
      }, 100);
    }
  });

  mo.observe(document.body, { childList: true, subtree: true });
}


// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  // Track which elements are currently visible — avoids per-element scroll listeners
  const visibleElements = new Set();

  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleElements.add(entry.target);
      } else {
        visibleElements.delete(entry.target);
      }
    });
  });

  animationTriggerElements.forEach((element) => {
    // Set initial zoom before scroll
    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    visibilityObserver.observe(element);
  });

  // Single shared scroll listener for all zoom elements
  window.addEventListener(
    'scroll',
    throttle(() => {
      visibleElements.forEach((element) => {
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      });
    }),
    { passive: true }
  );
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Use 'load' instead of 'DOMContentLoaded' to ensure layout is fully settled
// before IntersectionObserver measures element positions
window.addEventListener('load', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  watchForNewScrollTriggers();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
